import React, { useState, useRef, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "core-js/full/promise/with-resolvers.js";
import { Modal, Button, Input, Slider, Layout, Menu, Tooltip, Select } from "antd";
import type { MenuProps } from "antd";
import {
	ZoomInOutlined,
	ZoomOutOutlined,
	DownloadOutlined,
	LeftOutlined,
	RightOutlined,
	FullscreenOutlined,
	MenuFoldOutlined,
	MenuUnfoldOutlined
} from "@ant-design/icons";

const { Option } = Select;
const { Sider, Content } = Layout;

// // Worker configuration using CDN
// const PDFJS_VERSION = pdfjs.version || "4.7.76";
// const WORKER_URL = `https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.js`;

// Polyfill for Promise.withResolvers
// @ts-ignore - We ignore TypeScript errors for this block
if (typeof Promise.withResolvers === "undefined") {
	// @ts-ignore
	Promise.withResolvers = function () {
		let resolve, reject;
		const promise = new Promise((res, rej) => {
			resolve = res;
			reject = rej;
		});
		return { promise, resolve, reject };
	};
}

const WORKER_URL = new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString();

const setupPdfWorker = () => {
	if (pdfjs.GlobalWorkerOptions.workerSrc !== WORKER_URL) {
		pdfjs.GlobalWorkerOptions.workerSrc = WORKER_URL;
	}
};

// Configure the pdf.js worker once, before any component renders.
setupPdfWorker();

interface ThumbnailProps {
	pageNumber: number;
	file: string;
	onPageChange: (page: number) => void;
}

const Thumbnail: React.FC<ThumbnailProps> = ({ pageNumber, file, onPageChange }) => {
	return (
		<div onClick={() => onPageChange(pageNumber)} style={{ cursor: "pointer", marginBottom: "10px" }}>
			<Document file={file}>
				<Page pageNumber={pageNumber} width={150} renderTextLayer={false} renderAnnotationLayer={false} />
			</Document>
			<div style={{ textAlign: "center" }}>Page {pageNumber}</div>
		</div>
	);
};

const MAX_THUMBNAILS = 50; // Maximum number of thumbnails to render

interface PDFViewerProps {
	pdfUrl: string;
	onCancel: () => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ pdfUrl, onCancel }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const pagesRef = useRef<HTMLDivElement>(null);
	const [numPages, setNumPages] = useState<number | null>(null);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [scale, setScale] = useState<number>(1.0);
	const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
	const [thumbnails, setThumbnails] = useState<number[]>([]);
	const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
	const [useImageThumbnails, setUseImageThumbnails] = useState<boolean>(true);
	const [zoomMode, setZoomMode] = useState<string>("custom");
	const [containerWidth, setContainerWidth] = useState<number>(0);
	const [pdfDefaultWidth, setPdfDefaultWidth] = useState<number>(800);
	const [pdfDefaultHeight, setPdfDefaultHeight] = useState<number>(800);
	const [containerHeight, setContainerHeight] = useState<number>(0);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState<number>(0);

	// Clear any previous error when a new PDF is opened.
	useEffect(() => {
		setLoadError(null);
	}, [pdfUrl]);

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
			if (containerRef.current) {
				setContainerWidth(containerRef.current.clientWidth);
				setContainerHeight(containerRef.current.clientHeight);
			}
		};
		window.addEventListener("resize", handleResize);
		handleResize();
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		if (numPages) {
			const newThumbnails = Array.from({ length: numPages }, (_, index) => index + 1);
			setThumbnails(newThumbnails);
			setUseImageThumbnails(numPages <= MAX_THUMBNAILS);
		} else {
			setThumbnails([]);
		}
	}, [numPages]);

	const handleLoadError = useCallback((error: Error) => {
		console.error("handleLoadError PDF load error:", error);
		setLoadError(error.message);
	}, []);

	// Loading component
	const LoadingComponent = () => (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
				height: "200px"
			}}
		>
			<div>Loading PDF...</div>
		</div>
	);

	const onDocumentLoadSuccess = async pdfObject => {
		const { numPages } = pdfObject;

		try {
			const page1 = await pdfObject.getPage(1);
			setPdfDefaultWidth(page1.view[2]);
			setPdfDefaultHeight(page1.view[3]);
		} catch (error) {
			console.log("getPage error: ", error);
		}

		setNumPages(numPages);
		setPageNumber(1);
	};

	const scrollToPage = (page: number) => {
		if (pagesRef.current) {
			const pageElement = pagesRef.current.querySelector(`[data-page-number="${page}"]`);
			if (pageElement) {
				pageElement.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}
	};

	const changePage = (newPage: number) => {
		if (numPages && newPage >= 1 && newPage <= numPages) {
			setPageNumber(newPage);
			scrollToPage(newPage);
		}
	};

	const handleZoom = (newScale: number) => {
		setScale(Math.max(0.5, Math.min(2, newScale)));
		setZoomMode("custom");
	};

	const updateScale = useCallback(
		(mode: string) => {
			if (containerWidth && containerHeight && pdfDefaultWidth && pdfDefaultHeight) {
				if (mode === "fitToWidth") {
					const scaleX = containerWidth / pdfDefaultWidth;

					setScale(scaleX);
				} else if (mode === "fitToPage") {
					const scaleX = containerWidth / pdfDefaultWidth;
					const scaleY = containerHeight / pdfDefaultHeight;

					setScale(Math.min(scaleX, scaleY));
				}
			}
		},
		[containerWidth, containerHeight, pdfDefaultWidth, pdfDefaultHeight]
	);

	const handleZoomModeChange = (mode: string) => {
		setZoomMode(mode);
		if (mode === "fitToPage" || mode === "fitToWidth") {
			updateScale(mode);
			setSidebarCollapsed(true);
		}
	};

	const toggleFullScreen = () => {
		if (!document.fullscreenElement) {
			if (containerRef.current?.requestFullscreen) {
				containerRef.current.requestFullscreen();
			} else if ((containerRef.current as any)?.mozRequestFullScreen) {
				// Firefox
				(containerRef.current as any).mozRequestFullScreen();
			} else if ((containerRef.current as any)?.webkitRequestFullscreen) {
				// Chrome, Safari and Opera
				(containerRef.current as any).webkitRequestFullscreen();
			} else if ((containerRef.current as any)?.msRequestFullscreen) {
				// IE/Edge
				(containerRef.current as any).msRequestFullscreen();
			}
		} else {
			if (document.exitFullscreen) {
				document.exitFullscreen();
			} else if ((document as any).mozCancelFullScreen) {
				// Firefox
				(document as any).mozCancelFullScreen();
			} else if ((document as any).webkitExitFullscreen) {
				// Chrome, Safari and Opera
				(document as any).webkitExitFullscreen();
			} else if ((document as any).msExitFullscreen) {
				// IE/Edge
				(document as any).msExitFullscreen();
			}
		}
	};

	const handleScroll = useCallback(() => {
		if (pagesRef.current) {
			const { scrollTop, clientHeight, scrollHeight } = pagesRef.current;
			const pageHeight = scrollHeight / (numPages || 1);
			const currentPage = Math.floor(scrollTop / pageHeight) + 1;
			setPageNumber(currentPage);
		}
	}, [numPages]);

	useEffect(() => {
		const pagesElement = pagesRef.current;
		if (pagesElement) {
			pagesElement.addEventListener("scroll", handleScroll);
			return () => pagesElement.removeEventListener("scroll", handleScroll);
		}
	}, [handleScroll]);

	const isSmallScreen = windowWidth <= 767;

	const menuItems: MenuProps["items"] = thumbnails.map(page => ({
		key: page.toString(),
		label: useImageThumbnails ? (
			<Thumbnail pageNumber={page} file={pdfUrl} onPageChange={changePage} />
		) : (
			`Page ${page}`
		),
		onClick: () => changePage(page)
	}));

	return (
		<div>
			<Modal
				open={!!pdfUrl}
				onCancel={onCancel}
				width={isSmallScreen ? "95%" : "100%"}
				style={{ top: 0, paddingBottom: 0 }}
				styles={{ body: { height: "calc(100vh - 110px)", padding: 0 } }}
				footer={null}
				className="attachment-modal"
			>
				<Layout ref={containerRef} className="pdf-layout">
					{
						// !sidebarCollapsed && thumbnails.length > 0 &&
						thumbnails.length > 0 && (
							<Sider
								collapsible
								collapsed={sidebarCollapsed}
								onCollapse={collapsed => setSidebarCollapsed(collapsed)}
								collapsedWidth={0}
								zeroWidthTriggerStyle={{ top: 5 }}
								theme="light"
								width={200}
								breakpoint="lg"
							>
								<Menu className="pdf-menu" mode="inline" items={menuItems} />
							</Sider>
						)
					}
					<Layout>
						<div className="pdf-toolbar-container">
							<div
								className="page-navigation"
								style={{
									marginBottom: isSmallScreen ? "10px" : "0"
								}}
							>
								<Button icon={<LeftOutlined />} onClick={() => changePage(pageNumber - 1)} disabled={pageNumber <= 1} />
								<Input
									className="page-input"
									value={pageNumber}
									onChange={e => {
										const newPage = Number(e.target.value);
										if (!isNaN(newPage)) {
											changePage(newPage);
										}
									}}
								/>
								<span className="page-divider">/ {numPages}</span>
								<Button
									icon={<RightOutlined />}
									onClick={() => changePage(pageNumber + 1)}
									disabled={pageNumber >= (numPages || 0)}
								/>
							</div>
							<div className="right-toolbar-container">
								<Select value={zoomMode} className="select-zoom-mode" onChange={handleZoomModeChange}>
									<Option value="fitToPage">Fit to Page</Option>
									<Option value="fitToWidth">Fit to Width</Option>
									<Option value="custom">Custom</Option>
								</Select>
								<Tooltip title="Zoom Out">
									<Button icon={<ZoomOutOutlined />} onClick={() => handleZoom(scale - 0.1)} />
								</Tooltip>
								<Slider
									style={{ width: "100px", margin: "0 10px" }}
									min={0.5}
									max={2}
									step={0.1}
									value={scale}
									onChange={handleZoom}
								/>
								<Tooltip title="Zoom In">
									<Button icon={<ZoomInOutlined />} onClick={() => handleZoom(scale + 0.1)} />
								</Tooltip>
								<Tooltip title="Download">
									<Button icon={<DownloadOutlined />} onClick={() => window.open(pdfUrl, "_blank")} />
								</Tooltip>
								<Tooltip title="Fullscreen">
									<Button icon={<FullscreenOutlined />} onClick={toggleFullScreen} />
								</Tooltip>
								{thumbnails.length > 0 && (
									<Tooltip title={sidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}>
										<Button
											icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
											onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
										/>
									</Tooltip>
								)}
							</div>
						</div>
						<Content className="pdf-content">
							<div ref={pagesRef} className="pdf-document-container">
								<Document
									key={reloadKey}
									file={pdfUrl}
									onLoadSuccess={onDocumentLoadSuccess}
									onLoadError={handleLoadError}
									loading={<LoadingComponent />}
									error={
										loadError ? (
											<div style={{ textAlign: "center", padding: "20px" }}>
												<p>Error loading PDF. Please try:</p>
												<div
													style={{
														display: "flex",
														gap: "10px",
														justifyContent: "center"
													}}
												>
													<Button onClick={() => window.open(pdfUrl, "_blank")}>Open in new tab</Button>
													<Button
														onClick={() => {
															setLoadError(null);
															setReloadKey(key => key + 1);
														}}
													>
														Retry
													</Button>
												</div>
											</div>
										) : null
									}
								>
									{Array.from(new Array(numPages), (el, index) => (
										<div key={`page_${index + 1}`} className="pdf-page-container" data-page-number={index + 1}>
											<Page
												pageNumber={index + 1}
												scale={scale}
												onLoadSuccess={page => {
													if (scale === 1.0) {
														setPdfDefaultWidth(page.width);
														setPdfDefaultHeight(page.height);
													}
												}}
											/>
										</div>
									))}
								</Document>
							</div>
						</Content>
					</Layout>
				</Layout>
			</Modal>
		</div>
	);
};

export default PDFViewer;
